/**
 * What the signed-in user may do in the current company and project —
 * derived once from `GET user` (plus the `GET roles` dictionary) and the
 * workspace ids, as plain booleans the screens read instead of comparing role
 * codes themselves.
 *
 * The rules mirror the backend (#1836, branch `new_roles`): the route matrix
 * in `config/permissions.php`, which grants the UNION of the user's company
 * role and their role in the project, narrowed by the form requests'
 * `authorize()` (project update/delete, employee invitations). The server
 * stays the authority; this module only decides what to show. Pure: no React,
 * no fetching — the hook in `@/hooks/usePermissions` feeds it.
 *
 * @module access/permissions
 */

import { userRole } from '@/enums';
import type { IRoles, IUser } from '@/types';
import { asRoleCode, isCompanyWideRole } from './roles';

/** The user's standing in the current company. */
export interface CompanyAccess {
    code: userRole | null;
    /** Holds `company_owner` — the backend's wildcard role. */
    isOwner: boolean;
}

/** The user's standing in the current project: their own project role, or
 *  — for a company owner / admin — the company role that covers every
 *  project of the company, exactly as `GET user` reports it. */
export interface ProjectAccess {
    code: userRole | null;
    /** The row comes from a company-wide role, not from `users_projects`. */
    companyWide: boolean;
}

export interface Permissions {
    userId: number | null;
    /** The user's standing in the workspace is known: `GET user` is in, and
     *  no membership row still waits for `GET roles` to name its `role_id`.
     *  Until then every flag below reads false — «nothing yet», not «no». */
    resolved: boolean;
    company: CompanyAccess;
    project: ProjectAccess;

    /* ---- company ---- */
    /** Invite, re-role and remove company members. */
    canManageCompanyMembers: boolean;
    /** Company name, contacts, logo. */
    canEditCompany: boolean;
    canDeleteCompany: boolean;
    canCreateProject: boolean;
    /** A company admin creates projects for others: one of the participants
     *  should be made the project's owner. */
    mustAssignProjectOwner: boolean;

    /* ---- project ---- */
    /** Project card, archive (`UpdateProjectRequest`). */
    canEditProject: boolean;
    /** `POST projects/:id/copy` — the route matrix alone (no form-request
     *  check), so it follows the role in THAT project: read it through a
     *  per-project derivation in a list of projects. */
    canCopyProject: boolean;
    /** Soft delete (`SoftDeleteProjectRequest`): the company owner only. */
    canDeleteProject: boolean;
    /** Archive, delete and restore any project of the company, and see the
     *  archived/deleted lists. Unlike `canDeleteProject` it needs no project
     *  in hand: the backend checks the company role alone. */
    canManageProjectTrash: boolean;
    /** `POST projects/:id/assignees`. */
    canManageProjectMembers: boolean;

    /* ---- content ---- */
    /** Scenes, atoms, characters, lines, locations… */
    canEditContent: boolean;
    /** Create notes — wider than content: an editor reads the script but
     *  leaves notes on it. A note is still editable only by its author
     *  (`@/access/notes`). */
    canAuthorNotes: boolean;
    /** «Добавить в словарь» — the project's spelling dictionary
     *  (`SpellingWordController`): every member reads it, only the owners and
     *  the editor write. */
    canEditDictionary: boolean;
    canReadComments: boolean;
    canComment: boolean;
    canReadVersions: boolean;
    canManageVersions: boolean;
}

export interface PermissionsInput {
    user: IUser | undefined;
    /** `GET roles` — resolves a `role_id` to its code: project rows of
     *  `GET user` carry the id alone. */
    roles?: IRoles | undefined;
    companyId: number | null | undefined;
    projectId: number | null | undefined;
}

/** What a single role grants inside a project. */
interface ProjectGrants {
    content: boolean;
    /** Write text atoms — which is all a note is, marker included (the
     *  scene body is a text atom too). */
    notes: boolean;
    /** Write the project's spelling words. */
    dictionary: boolean;
    comment: boolean;
    readComments: boolean;
    manageVersions: boolean;
    editProject: boolean;
    manageMembers: boolean;
    copyProject: boolean;
}

const NO_GRANTS: ProjectGrants = {
    content: false,
    notes: false,
    dictionary: false,
    comment: false,
    readComments: false,
    manageVersions: false,
    editProject: false,
    manageMembers: false,
    copyProject: false,
};

const ALL_GRANTS: ProjectGrants = {
    content: true,
    notes: true,
    dictionary: true,
    comment: true,
    readComments: true,
    manageVersions: true,
    editProject: true,
    manageMembers: true,
    copyProject: true,
};

/**
 * The route matrix, per code. `company_owner` is the wildcard; a company
 * admin reads the project (comments and versions included) and runs its card
 * and its members but writes no content; `company_employee` has no project
 * rights of its own. `project_admin` reads the script — every content
 * controller, `TextAtomController` included, is index/show only, so no notes
 * either — but comments, runs the versions and the project's members
 * (`assignees` / `updateAssignees`); not the project card —
 * `UpdateProjectRequest` doesn't name it. `copy` is named for the
 * company admin and nobody else below the owners. An `editor` reads: every
 * content controller is index/show only except `TextAtomController`, which
 * is what lets them leave notes; comments are open. The spelling dictionary
 * is written by the owners and the editor, read by both admins. The retired
 * scriptwriter codes keep their old grants for any membership the remap
 * missed — but not the dictionary, which came after them; `reviewer`'s key is
 * gone, so it grants nothing.
 */
const GRANTS: Readonly<Record<userRole, ProjectGrants>> = {
    [userRole.company_owner]: ALL_GRANTS,
    [userRole.company_admin]: { ...NO_GRANTS, readComments: true, editProject: true, manageMembers: true, copyProject: true },
    [userRole.company_employee]: NO_GRANTS,
    [userRole.project_owner]: ALL_GRANTS,
    [userRole.project_admin]: { ...NO_GRANTS, comment: true, readComments: true, manageVersions: true, manageMembers: true },
    [userRole.main_scriptwriter]: { ...ALL_GRANTS, copyProject: false, dictionary: false },
    [userRole.scriptwriter]: { ...ALL_GRANTS, editProject: false, manageMembers: false, copyProject: false, dictionary: false },
    [userRole.editor]: { ...NO_GRANTS, notes: true, dictionary: true, comment: true, readComments: true },
    [userRole.reviewer]: NO_GRANTS,
};

const NO_COMPANY: CompanyAccess = { code: null, isOwner: false };
const NO_PROJECT: ProjectAccess = { code: null, companyWide: false };

function codeOf(roleCode: string | null | undefined, roleId: number | null | undefined, roles: IRoles | undefined): userRole | null {
    const byCode = asRoleCode(roleCode);
    if (byCode !== null || roleId === null || roleId === undefined || !roles) return byCode;
    return asRoleCode([...roles.company, ...roles.project].find(role => role.id === roleId)?.code);
}

/** A row that names its role by id alone and the dictionary isn't here to
 *  read it (still loading, or failed). */
function waitsForRoles(roleCode: string | null | undefined, roleId: number | null | undefined, roles: IRoles | undefined): boolean {
    return !roles && !!roleId && asRoleCode(roleCode) === null;
}

/** Mirrors the lookups of {@link companyAccessOf} / {@link projectAccessOf}:
 *  a company-wide role never reads the project row, so it can't wait on it. */
function isResolved(input: PermissionsInput, company: CompanyAccess): boolean {
    const { user, roles, companyId, projectId } = input;
    if (!user) return false;
    const companyRow = companyId ? user.companies.find(c => c.id === companyId) : undefined;
    if (companyRow && waitsForRoles(companyRow.role_code, companyRow.role_id, roles)) return false;
    if (isCompanyWideRole(company.code)) return true;
    const projectRow = projectId ? user.projects.find(p => p.id === projectId) : undefined;
    return !(projectRow && waitsForRoles(projectRow.role_code, projectRow.role_id, roles));
}

function companyAccessOf(input: PermissionsInput): CompanyAccess {
    const { user, roles, companyId } = input;
    if (!user || !companyId) return NO_COMPANY;
    const membership = user.companies.find(c => c.id === companyId);
    if (!membership) return NO_COMPANY;
    const code = codeOf(membership.role_code, membership.role_id, roles);
    return { code, isOwner: code === userRole.company_owner };
}

function projectAccessOf(input: PermissionsInput, company: CompanyAccess): ProjectAccess {
    const { user, roles, projectId } = input;
    if (!user || !projectId) return NO_PROJECT;

    // A company owner / admin is on every project of the company, whatever
    // `users_projects` says (`GET user` overwrites such rows with the company
    // role and `company_effective`).
    if (isCompanyWideRole(company.code)) return { code: company.code, companyWide: true };

    const membership = user.projects.find(p => p.id === projectId);
    if (!membership) return NO_PROJECT;
    return { code: codeOf(membership.role_code, membership.role_id, roles), companyWide: false };
}

function grantsOf(code: userRole | null): ProjectGrants {
    return code === null ? NO_GRANTS : GRANTS[code];
}

export function derivePermissions(input: PermissionsInput): Permissions {
    const company = companyAccessOf(input);
    const project = projectAccessOf(input, company);

    // The backend checks every role the user holds for the request's project:
    // the company role and the project role, whichever grants.
    const own = grantsOf(project.code);
    const viaCompany = grantsOf(company.code);
    const grant = (key: keyof ProjectGrants) => project.code !== null && (own[key] || viaCompany[key]);

    const companyAdmin = company.code === userRole.company_admin;
    const companyMember = company.code !== null;

    return {
        userId: input.user?.id ?? null,
        resolved: isResolved(input, company),
        company,
        project,

        canManageCompanyMembers: company.isOwner || companyAdmin,
        canEditCompany: company.isOwner,
        canDeleteCompany: company.isOwner,
        canCreateProject: companyMember,
        mustAssignProjectOwner: companyAdmin,

        canEditProject: grant('editProject'),
        canCopyProject: grant('copyProject'),
        canDeleteProject: project.code !== null && company.isOwner,
        canManageProjectTrash: company.isOwner,
        canManageProjectMembers: grant('manageMembers'),

        canEditContent: grant('content'),
        canAuthorNotes: grant('notes'),
        canEditDictionary: grant('dictionary'),
        canReadComments: grant('readComments'),
        canComment: grant('comment'),
        // Every role may list a project's versions (`SnapshotController@index`).
        canReadVersions: project.code !== null,
        canManageVersions: grant('manageVersions'),
    };
}
